import type { ReactNode } from "react";

export interface StatCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
  iconAlt: string;
}

export const StatCard = ({
  title,
  value,
  subtitle,
  icon,
  iconAlt,
}: StatCardProps): JSX.Element => (
  <div className="flex min-w-0 flex-1 flex-col items-start gap-2 rounded-lg border border-[#222] p-4">
    <p className="w-full text-sm font-medium leading-5 text-[#45556c]">
      {title}
    </p>
    <div className="flex h-11 w-full items-end justify-between">
      <div className="flex min-w-0 flex-col items-start gap-1">
        <p className="text-base font-bold leading-6 text-[#0a0a0a]">{value}</p>
        <p className="whitespace-nowrap text-xs font-normal leading-4 text-[#62748e]">
          {subtitle}
        </p>
      </div>
      <img alt={iconAlt} className="size-8 shrink-0" src={icon} />
    </div>
  </div>
);

export interface ActionCardProps {
  /** Used when `iconSlot` is not provided */
  icon?: string;
  iconAlt?: string;
  /** Replaces the default 48×48 image (e.g. slate box + vector icon). */
  iconSlot?: ReactNode;
  title: string;
  subtitle: string;
  variant: "dark" | "light";
  badge?: number;
  onClick?: () => void;
  /** Extra classes on the outer button (e.g. border on dark variant). */
  className?: string;
}

export const ActionCard = ({
  icon,
  iconAlt,
  iconSlot,
  title,
  subtitle,
  variant,
  badge,
  onClick,
  className,
}: ActionCardProps): JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex h-auto min-h-[72px] w-full min-w-0 flex-1 cursor-pointer touch-manipulation items-center gap-3 rounded-xl px-4 py-3.5 text-left transition-opacity hover:opacity-90 active:opacity-95 sm:h-[88px] sm:min-h-[88px] sm:gap-4 sm:rounded-[14px] sm:px-5 sm:py-0 ${
      variant === "dark"
        ? "bg-[#222] shadow-[0px_10px_15px_-3px_rgba(34,34,34,0.2),0px_4px_6px_-4px_rgba(34,34,34,0.2)]"
        : "border border-[#eee] bg-white"
    } ${className ?? ""}`}
  >
    <div className="relative flex size-10 shrink-0 items-center justify-center sm:size-12">
      {iconSlot ?? (
        <img
          alt={iconAlt ?? ""}
          className="block size-10 sm:size-12"
          src={icon ?? ""}
        />
      )}
      {badge !== undefined && (
        <span className="absolute -top-0.5 left-6 z-[1] flex min-h-5 min-w-5 items-center justify-center rounded-full bg-[#fb2c36] px-1 text-center text-[10px] font-bold leading-4 text-white sm:-top-1 sm:left-8 sm:text-xs">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </div>
    <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5 py-0.5 sm:py-0">
      <p
        className={`w-full truncate text-base font-semibold leading-snug sm:text-lg sm:leading-[27px] ${
          variant === "dark" ? "text-white" : "text-[#222]"
        }`}
      >
        {title}
      </p>
      <p
        className={`w-full truncate text-[11px] font-medium leading-snug sm:text-xs sm:leading-4 ${
          variant === "dark" ? "text-[#f0f0f0]" : "text-[#90a1b9]"
        }`}
      >
        {subtitle}
      </p>
    </div>
  </button>
);
